import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

// Resolve the Node API base URL (same pattern as the rest of the app).
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (n, d = 2) =>
  n === null || n === undefined || isNaN(n) ? '—' : Number(n).toFixed(d);

const verdictColor = (v) => {
  switch ((v || '').toUpperCase()) {
    case 'PROCEED': return 'text-green-700 bg-green-100';
    case 'HOLD': return 'text-yellow-700 bg-yellow-100';
    case 'REDUCE': return 'text-orange-700 bg-orange-100';
    case 'SKIP': return 'text-red-700 bg-red-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

const Watchlist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Searchable picker (symbol + name), same source as the Analyze page.
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pickerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/watchlist?cache=${Date.now()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setItems(Array.isArray(json) ? json : []);
    } catch (e) {
      toast.error('Could not load your watchlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load the symbol/name list once for the add-picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/stock-list`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && Array.isArray(json)) setOptions(json);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const add = async (symbol) => {
    const sym = (symbol || '').toUpperCase().trim();
    if (!sym) return;
    setBusy(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/watchlist?symbol=${encodeURIComponent(sym)}`, {
        method: 'POST', headers: authHeaders(),
      });
      const json = await res.json();
      if (json.status === 'ok') { toast.success(`${sym} added`); setQuery(''); setOpen(false); await load(); }
      else toast.error(json.message === 'unknown symbol' ? `Unknown symbol: ${sym}` : 'Could not add');
    } catch (_) { toast.error('Could not add'); }
    finally { setBusy(false); }
  };

  const remove = async (symbol) => {
    setBusy(true);
    try {
      await fetch(`${getApiUrl()}/api/watchlist?symbol=${encodeURIComponent(symbol)}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      setItems((prev) => prev.filter((i) => i.symbol !== symbol));
    } catch (_) { toast.error('Could not remove'); }
    finally { setBusy(false); }
  };

  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    const starts = [], contains = [];
    for (const o of options) {
      const s = (o.symbol || '').toLowerCase(); const n = (o.name || '').toLowerCase();
      if (s.startsWith(q)) starts.push(o);
      else if (s.includes(q) || n.includes(q)) contains.push(o);
      if (starts.length >= 50) break;
    }
    return starts.concat(contains).slice(0, 50);
  })();

  // --- Watchlist table: filter + sortable columns (client-side; items already loaded) ---
  const [tableFilter, setTableFilter] = useState('');
  const [sort, setSort] = useState({ key: 'symbol', dir: 'asc' }); // key + 'asc'|'desc'

  // AI verdict sorts by conviction priority (not alphabetically), matching the rest of the app.
  const VERDICT_RANK = { PROCEED: 1, HOLD: 2, REDUCE: 3, SKIP: 4 };
  const verdictRank = (v) => VERDICT_RANK[(v || '').toUpperCase()] || 9;

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const sortIndicator = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const visibleItems = (() => {
    const q = tableFilter.trim().toLowerCase();
    let rows = items;
    if (q) {
      rows = rows.filter(
        (it) => (it.symbol || '').toLowerCase().includes(q) || (it.name || '').toLowerCase().includes(q)
      );
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (it) => {
      switch (sort.key) {
        case 'name': return (it.name || '').toLowerCase();
        case 'close': return it.close == null ? null : Number(it.close);
        case 'ai_verdict': return verdictRank(it.ai_verdict);
        case 'last_signal': return (it.last_signal || '').toLowerCase();
        case 'symbol':
        default: return (it.symbol || '').toLowerCase();
      }
    };
    // Copy before sort (don't mutate state); nulls always sort last regardless of dir.
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  })();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Watchlist</h1>
        <p className="text-gray-500 mb-6">Track stocks and see their latest signal and AI verdict at a glance.</p>

        {/* Add picker */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 relative max-w-md" ref={pickerRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">Add a stock</label>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search by symbol or company name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoComplete="off"
          />
          {open && filtered.length > 0 && (
            <ul className="absolute z-30 mt-1 left-4 right-4 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg text-sm">
              {filtered.map((o) => (
                <li key={o.symbol}>
                  <button type="button" disabled={busy} onClick={() => add(o.symbol)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 disabled:opacity-50">
                    <span className="font-semibold text-gray-900 min-w-[64px]">{o.symbol}</span>
                    <span className="text-gray-500 truncate">{o.name || 'N/A'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Your watchlist is empty</h3>
            <p className="text-gray-600">Add stocks above to start tracking them.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm">
            {/* Filter the watchlist rows (distinct from the add-picker above). */}
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                placeholder="Filter watchlist by symbol or name…"
                className="w-full max-w-sm px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  {[
                    ['symbol', 'Symbol'],
                    ['name', 'Name'],
                    ['close', 'Price'],
                    ['ai_verdict', 'AI Verdict'],
                    ['last_signal', 'Latest Signal'],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => toggleSort(key)}
                      className="px-4 py-2 cursor-pointer select-none hover:text-gray-900 whitespace-nowrap"
                      aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      {label}{sortIndicator(key)}
                    </th>
                  ))}
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No stocks match “{tableFilter}”.</td></tr>
                ) : visibleItems.map((it) => (
                  <tr key={it.symbol} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 font-semibold">
                      <Link to={`/stock/stock-analyze/${it.symbol}`} className="text-blue-600 hover:underline">{it.symbol}</Link>
                    </td>
                    <td className="px-4 py-2 text-gray-700 truncate max-w-[220px]">{it.name || 'N/A'}</td>
                    <td className="px-4 py-2">{it.close != null ? `$${fmt(it.close)}` : '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${verdictColor(it.ai_verdict)}`}>
                        {it.ai_verdict || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {it.last_signal ? <span title={it.last_signal_date}>{it.last_signal}</span> : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button disabled={busy} onClick={() => remove(it.symbol)}
                        className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50">Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">Informational only — not investment advice.</p>
      </div>
    </div>
  );
};

export default Watchlist;
